import { CoordinatorService } from './coordinator.service';
import { License } from './license';

export type TIER = 'free' | 'basic' | 'professional' | 'enterprise' | 'academic';
export const tierLs: TIER[] = ['free', 'basic', 'professional', 'enterprise', 'academic'];

export type CHECK_LICENSE_RESULT = {
    isPermitted: boolean;
    reason?: string;
    cloudProjectId?: string;
};

/**
 * License management service
 * Fetches and manages user license from license_get cloud function
 */
export class License$ {
    isReady = false;
    enforceLicense = false;

    // License is nullable until first fetch completes
    private _license: License | null = null;

    constructor(public coords: CoordinatorService) {}

    get license(): License | null {
        return this._license;
    }

    get currentTier(): TIER {
        return this._license?.tier || 'free';
    }

    /**
     * Safe getter for license that always returns a valid License
     * Falls back to a minimal free license if not yet loaded
     */
    get licenseOrFree(): License {
        if (this._license) return this._license;

        // Return minimal free license while loading
        return new License(this.coords, {
            tier: 'free',
            name: 'Free License',
            valid: true,
            status: 'active',
            userId: this.coords.user$.currentId || 'unknown',
            licenseId: 'temporary-free',
            issuedAt: new Date().toISOString(),
            validUntil: '2099-12-31T23:59:59.999Z',
            features: {
                maxGraphs: 3,
                maxNodes: 100,
                storageLimit: '100MB',
                apiCallsPerDay: 1000,
                premiumSupport: false,
                exportFormats: ['json'],
                collaborators: 0,
            },
            limitations: {
                watermark: true,
                analytics: false,
                customBranding: false,
            },
        });
    }

    private async _checkFree(): Promise<CHECK_LICENSE_RESULT> {
        const resp = await this.coords.firebase$.callFunction('hasCloudProject', {});

        console.warn('LICENSE - Free tier check result:', resp);

        if (resp === 'ERROR')
            return {
                isPermitted: false,
                reason: 'quotaFreeExceeded',
            };

        const data = resp.data as any;

        // the free user is permitted a single cloud project,
        // if the response is true, the quota is exceeded

        if (data.hasCloudProject) {
            return {
                isPermitted: false,
                reason: 'quotaFreeExceeded',
                cloudProjectId: data.cloudProjectId,
            };
        }

        return {
            isPermitted: true,
        };
    }

    private async _checkBasic(): Promise<CHECK_LICENSE_RESULT> {
        const count = await this.countTeamProjects();
        console.warn('LICENSE - Basic tier check result:', count);
        const ownerCount = count.owner;
        const contributorCount: number = count.contributor;
        const teamCount = ownerCount + contributorCount;
        if (teamCount > 5)
            return {
                isPermitted: false,
                reason: 'teamCountExceeded',
            };
        return {
            isPermitted: true,
        };
    }

    async checkLicense(): Promise<CHECK_LICENSE_RESULT> {
        switch (this.currentTier) {
            case 'free':
                return await this._checkFree();
            case 'basic':
                return await this._checkBasic();
            default:
                return {
                    isPermitted: true,
                };
        }
    }

    /**
     * Fetch license from server via license_get cloud function
     * @param forceRefresh If true, bypasses server cache
     */
    async get(forceRefresh = false) {
        try {
            console.log('📄 Fetching license from server...');

            const resp = await this.coords.firebase$.callFunction('license_get', {
                forceRefresh,
            });

            if (!resp || resp === 'ERROR') {
                console.warn('⚠️ Failed to retrieve license, using fallback');
                this._setFallbackLicense();
                return;
            }

            console.log('✅ License retrieved successfully');
            this._license = new License(this.coords, resp.data as any);
            this.isReady = true;
            this.coords.display$.markForCheck();
        } catch (error) {
            console.error('❌ License fetch error:', error);
            this._setFallbackLicense();
        }
    }

    /**
     * Sets a fallback free license when server fetch fails
     */
    private _setFallbackLicense() {
        const userId = this.coords.user$.currentId || 'unknown';
        this._license = new License(this.coords, {
            tier: 'free',
            name: 'Free License',
            valid: true,
            status: 'active',
            userId,
            licenseId: 'fallback-free',
            issuedAt: new Date().toISOString(),
            validUntil: '2099-12-31T23:59:59.999Z',
            features: {
                maxGraphs: 3,
                maxNodes: 100,
                storageLimit: '100MB',
                apiCallsPerDay: 1000,
                premiumSupport: false,
                exportFormats: ['json'],
                collaborators: 0,
            },
            limitations: {
                watermark: true,
                analytics: false,
                customBranding: false,
            },
        });
        this.isReady = true;
        this.coords.display$.markForCheck();
    }

    /**
     * Refresh license from server (always forces refresh)
     * @param closeLicenseManager If true, closes license manager UI after refresh
     */
    async refresh(closeLicenseManager = false) {
        await this.get(true);
        if (closeLicenseManager) {
            this.coords.isLicenseManager = false;
        }
    }

    async countTeamProjects() {
        const resp = await this.coords.firebase$.callFunction('countTeamProjects', {});
        console.warn('LICENSE - Count team projects result:', resp);
        if (resp === 'ERROR') return 0;
        return resp.data as any;
    }

    async checkTeamProjects() {
        const resp = await this.coords.firebase$.callFunction('checkTeamProjects', {});
        console.warn('LICENSE - Check team projects result:', resp);
        if (resp === 'ERROR') return false;

        return resp.data as any;
    }
}
